import type Context from '@/context';
import type { ClipboardData } from '@/models/clipboard/ClipboardData';
import { parseClipChipRef } from '@/models/clipboard/ClipboardData';
import { Point } from '@/models/Point';
import { Rotation, rotatePointAroundOrigin } from '@/models/data/Rotation';
import type { InterfaceTileState } from '@/schemas/tiles';
import { isSuppliedChallengeInterface, isPendingChallengeInterface } from './editing';
import { chipBudget } from './chips';

/** Run at paste entry and commit, never in the hover/render loop. */
export function restrictChallengeInterfacePaste(
  context: Context,
  data: ClipboardData,
): ClipboardData {
  if (
    !context.challenge ||
    context.challengeAuthoring ||
    !data.tiles.some((entry) => isSuppliedChallengeInterface(entry.tileType))
  )
    return data;
  const portsByIndex = new Map<number, Map<string, string>>();
  const tiles = data.tiles.filter((entry) => {
    if (!isSuppliedChallengeInterface(entry.tileType)) return true;
    const index = entry.meta.partOfChip ? parseClipChipRef(entry.meta.partOfChip) : null;
    if (index === null)
      return (
        data.operation === 'cut' &&
        (entry.state as InterfaceTileState<unknown> | undefined)?.connectedInterface
          ?.chipInstanceId === context.mainChip.editingInstance.id &&
        isPendingChallengeInterface(
          context,
          context.editor.currentCircuit.tileManagerModel,
          entry.tileType,
          entry.state,
        )
      );
    let ports = portsByIndex.get(index);
    if (!ports) {
      ports = new Map();
      portsByIndex.set(index, ports);
      let record: ClipboardData['chipInstances'][number] | undefined;
      for (const candidate of data.chipInstances) {
        if (candidate.localIndex !== index) continue;
        if (record) return false;
        record = candidate;
      }
      const chip = record && context.chipStore.getChip(record.chipId);
      if (!record || !chip || !chipBudget(context, chip.id)?.allowed) return false;
      const origin = chip.structureCircuit.originLocation;
      const offset = Point.fromData(record.relativeOrigin).sub(origin);
      for (const source of chip.structureCircuit.circuitModel.tiles) {
        if (!isSuppliedChallengeInterface(source.tileType)) continue;
        const location = rotatePointAroundOrigin(
          Point.fromData(source.location),
          origin,
          record.rotation ?? Rotation.CW_0,
        ).add(offset);
        const link = (source.state as InterfaceTileState<unknown>)?.connectedInterface
          ?.interfaceLinkId;
        if (link) ports.set(`${source.tileType}:${location.x},${location.y}`, link);
      }
    }
    const link = (entry.state as InterfaceTileState<unknown> | undefined)
      ?.connectedInterface?.interfaceLinkId;
    return (
      !!link &&
      ports.get(
        `${entry.tileType}:${entry.relativeLocation.x},${entry.relativeLocation.y}`,
      ) === link
    );
  });
  return tiles.length === data.tiles.length
    ? data
    : { ...data, tiles, ...(tiles.length ? {} : { chipInstances: [] }) };
}
